import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from 'react-modal';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { FiEdit, FiTrash } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import ConditionalButton from '../components/common/ConditionalButton';
import ConditionalActions from '../components/common/ConditionalActions';

const emptyType = { type_evaluation: '', abrev: '' };

const TypeEvaluationsPage = () => {
  const { user } = useAuth();
  const [types, setTypes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyType);

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    const res = await api.get('/type-evaluations');
    setTypes(res.data.data || []);
  };

  const handleAdd = () => {
    setEditing(null);
    setForm(emptyType);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({ type_evaluation: item.type_evaluation, abrev: item.abrev });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer ce type ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      await api.delete(`/type-evaluations/${id}`);
      fetchTypes();
      Swal.fire('Supprimé !', 'Le type d\'évaluation a été supprimé.', 'success');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditing(null);
    setForm(emptyType);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.type_evaluation) {
      await Swal.fire({ icon: 'error', title: 'Erreur', text: 'Le nom du type est obligatoire.' });
      return;
    }
    if (editing) {
      await api.put(`/type-evaluations/${editing.id}`, form);
    } else {
      await api.post('/type-evaluations', form);
    }
    fetchTypes();
    setShowModal(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Types d'évaluations</h1>
        <ConditionalButton
          entity="type_evaluations"
          permission="create"
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          onClick={handleAdd}
        >
          Nouveau type
        </ConditionalButton>
      </div>
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Abréviation</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {types.length === 0 ? (
              <tr>
                <td colSpan="3" className="px-6 py-4 text-center text-gray-500">Aucun type</td>
              </tr>
            ) : (
              types.map((item) => (
                <tr key={item.id}>
                  <td className="px-6 py-4">{item.type_evaluation}</td>
                  <td className="px-6 py-4">{item.abrev}</td>
                  <td className="px-6 py-4 text-right">
                    <ConditionalActions
                      entity="type_evaluations"
                      onEdit={() => handleEdit(item)}
                      onDelete={() => handleDelete(item.id)}
                      editIcon={FiEdit}
                      deleteIcon={FiTrash}
                      className="flex space-x-2"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Modal
        isOpen={showModal}
        onRequestClose={handleModalClose}
        contentLabel="Type d'évaluation"
        ariaHideApp={false}
        style={{
          overlay: {
            backgroundColor: 'rgba(0,0,0,0.6)', // plus sombre, ajuste l'opacité si besoin
            zIndex: 1000
          },
          content: {
            maxWidth: '400px',
            width: '100%',
            margin: 'auto',
            height: '300px',
            borderRadius: 12,
            overflow: 'hidden',
            padding: 0
          }
        }}
      >
        <form onSubmit={handleSubmit} className="p-6">
          <h2 className="text-lg font-semibold mb-4">{editing ? 'Modifier' : 'Nouveau'} type d'évaluation</h2>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Type *</label>
            <input
              type="text"
              name="type_evaluation"
              value={form.type_evaluation}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Abréviation</label>
            <input
              type="text"
              name="abrev"
              value={form.abrev}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              maxLength={10}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={handleModalClose} className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300">Annuler</button>
            <ConditionalButton
              entity="type_evaluations"
              permission={editing ? "update" : "create"}
              type="submit"
              className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700"
            >
              {editing ? 'Enregistrer' : 'Créer'}
            </ConditionalButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default TypeEvaluationsPage;
